import React, { useEffect } from "react";
import { Modal, Form, Input, Radio, Button, Switch } from "antd";
import { LuX } from "react-icons/lu";
import { CopyOutlined } from "@ant-design/icons";

const ENV_OPTIONS = [
  { value: "staging", label: "Staging" },
  { value: "production", label: "Production" },
];

const CopyFormEnvironmentModal = ({
  visible,
  onClose,
  onConfirm,
  formTitle = "",
  loading = false,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!visible) {
      return;
    }

    form.setFieldsValue({
      environment: "staging",
      title: formTitle || "",
      withResponses: false,
    });
  }, [visible, formTitle, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const title = String(values.title || "").trim();

      if (!title) {
        form.setFields([
          {
            name: "title",
            errors: ["Form title is required"],
          },
        ]);
        return;
      }

      await onConfirm?.({
        environment: values.environment,
        title,
        withResponses: Boolean(values.withResponses),
      });
    } catch {
      // validation errors are shown by the form
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      width={440}
      destroyOnClose
      closeIcon={
        <div className="bg-secondary h-8 w-8 flex justify-center items-center rounded-full">
          <LuX className="text-red-400" size={20} />
        </div>
      }
      className="rounded-2xl"
    >
      <div className="flex flex-col pt-2">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
            <CopyOutlined className="text-gray-700 text-lg" />
          </div>
          <h2 className="text-xl font-semibold mb-0 mt-0">Copy form</h2>
        </div>
        <p className="text-gray-500 text-sm mb-6">
          Create a new form in Staging or Production
        </p>
        <Form form={form} layout="vertical" requiredMark>
          <Form.Item
            name="environment"
            label="Environment"
            rules={[
              { required: true, message: "Please select an environment" },
            ]}
          >
            <Radio.Group className="flex flex-col gap-2 w-full">
              {ENV_OPTIONS.map((option) => (
                <Radio
                  key={option.value}
                  value={option.value}
                  className="!px-3 !py-2 !m-0 rounded-xl border border-gray-100 w-full"
                >
                  {option.label}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="title"
            label="New form title"
            required
            rules={[
              {
                required: true,
                whitespace: true,
                message: "Form title is required",
              },
            ]}
          >
            <Input
              placeholder="Enter title for the new form"
              className="rounded-xl h-11"
              maxLength={200}
            />
          </Form.Item>
          <div className="mb-0 flex items-center justify-between gap-3 rounded-xl border border-gray-100 px-3 py-2">
            <div>
              <span className="text-sm font-medium text-gray-900">
                With responses
              </span>
              <p className="mb-0 mt-0.5 text-xs text-gray-500">
                Include submitted responses in the copied form
              </p>
            </div>
            <Form.Item
              name="withResponses"
              valuePropName="checked"
              noStyle
            >
              <Switch className="custom-switch custom-switch--compact shrink-0" />
            </Form.Item>
          </div>
        </Form>
        <div className="flex gap-3 w-full mt-2">
          <Button
            onClick={onClose}
            disabled={loading}
            className="border-0 flex-1 py-3 px-4 rounded-xl bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 h-11"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            loading={loading}
            onClick={handleSubmit}
            className="flex-1 py-3 px-4 rounded-xl bg-[#18181B] hover:bg-black border-0 text-white font-medium h-11"
          >
            Create form
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CopyFormEnvironmentModal;
